<script lang="ts">
  import { cloneRoom, cloneScene, getErrorMessage } from '$lib/api';
  import { onMount } from 'svelte';
  import { ChevronRight, MoreHorizontal, Zap } from 'lucide-svelte';
  import ConfirmDialog from '../ui/ConfirmDialog.svelte';
  import { router, selectEntity, clearEntity } from '$lib/router';
  import type { EntityType, RouteEntity } from '$lib/router';
  import { computeUnitWorkflow } from '../../workflow';
  import { showSuccess, showError } from '../../state/toast.svelte';
  import { unitsQuery } from '../../queries/units';
  import { zonesQuery } from '../../queries/zones';
  import { roomsQuery } from '../../queries/rooms';
  import { loadsQuery } from '../../queries/loads';
  import { inputDevicesQuery } from '../../queries/input_devices';
  import { rcusQuery } from '../../queries/rcus';
  import { dinModulesForUnitQuery } from '../../queries/din_modules';
  import { scenesQuery } from '../../queries/scenes';
  import { unitSceneLoadsQuery } from '../../queries/scene_loads';
  import { rcuChannelsQuery } from '../../queries/rcu_channels';
  import { productsQuery } from '../../queries/products';
  import {
    invalidateAfterInputDeviceUpdate,
    invalidateAfterRoomCreate,
    invalidateAfterSceneCreate,
    invalidateAfterChannelDelete,
    invalidateAfterDinModuleCreate
  } from '../../state/invalidation';
  import {
    createZoneMutation,
    updateZoneMutation,
    deleteZoneMutation
  } from '../../mutations/zones';
  import {
    createRoomMutation,
    updateRoomMutation,
    deleteRoomMutation
  } from '../../mutations/rooms';
  import { updateLoadMutation, deleteLoadMutation } from '../../mutations/loads';
  import {
    createInputDeviceMutation,
    deleteInputDeviceMutation
  } from '../../mutations/input_devices';
  import {
    createSceneMutation,
    updateSceneMutation,
    deleteSceneMutation
  } from '../../mutations/scenes';
  import { updateRcuMutation, deleteRcuMutation } from '../../mutations/rcus';
  import { deleteDinModule } from '$lib/api/din_module';
  import { updateInputKey } from '$lib/api/input_key';
  import { dinModuleLabel } from '../../hardware';
  import { DEVICE_TYPE_LABELS } from '$lib/constants/device_types';
  import { createLoad, createDevice } from '../../unitCreate';
  import {
    regenerateLEDMappings,
    LEDMappingRegenerateConflict,
    deleteAllLEDScenes
  } from '$lib/api/led_scene';
  import { ledScenesQuery } from '../../queries/led_scenes';
  import { getQueryClient } from '../../state/queryClient';

  const units = unitsQuery();
  const zonesQ = zonesQuery();
  const roomsQ = roomsQuery();
  const loadsQ = loadsQuery();
  const devicesQ = inputDevicesQuery();
  const rcusQ = rcusQuery();
  const dinModulesQ = dinModulesForUnitQuery();
  const scenesQ = scenesQuery();
  const sceneLoadsQ = unitSceneLoadsQuery();
  const rcuChannelsQ = rcuChannelsQuery();
  const productsQ = productsQuery();
  const ledScenesQ = ledScenesQuery();

  const createZoneMut = createZoneMutation();
  const updateZoneMut = updateZoneMutation();
  const deleteZoneMut = deleteZoneMutation();
  const createRoomMut = createRoomMutation();
  const updateRoomMut = updateRoomMutation();
  const deleteRoomMut = deleteRoomMutation();
  const updateLoadMut = updateLoadMutation();
  const deleteLoadMut = deleteLoadMutation();
  const createInputDeviceMut = createInputDeviceMutation();
  const deleteInputDeviceMut = deleteInputDeviceMutation();
  const createSceneMut = createSceneMutation();
  const updateSceneMut = updateSceneMutation();
  const deleteSceneMut = deleteSceneMutation();
  const updateRcuMut = updateRcuMutation();
  const deleteRcuMut = deleteRcuMutation();

  const GROUP_SECTION_BADGES: Record<string, string[]> = {
    spaces: ['zones', 'loads'],
    hardware: ['hardware'],
    keypads: ['keypads']
  };

  let rowEls: Record<string, HTMLElement> = {};
  let railEl: HTMLDivElement | undefined;
  let focusedIndex = $state(-1);
  let openMenuKey = $state<string | null>(null);
  let showDeleteConfirm = $state(false);
  let deleteTarget = $state<{ type: string; id: number; label: string } | null>(null);

  // Folders start collapsed so the rail opens as a tidy list of top-level
  // groups; only the unit root stays open. Selecting an entity (deep link,
  // command palette, create flow) auto-expands the path to it — see the
  // reveal effect below.
  let expanded = $state<Record<string, boolean>>({ unit: true });

  let regeneratingLed = $state(false);
  let deletingAllLed = $state(false);

  let entity = $derived(router.selectedEntity);

  let unit = $derived((units.data ?? []).find((u) => u.id === router.unitId) ?? null);
  let unitId = $derived(router.unitId ?? 0);
  let unitZones = $derived(zonesQ.data ?? []);
  let unitRooms = $derived(roomsQ.data ?? []);
  let unitLoads = $derived(loadsQ.data ?? []);
  let unitDevices = $derived(devicesQ.data ?? []);
  let unitInputKeys = $derived((devicesQ.data ?? []).flatMap((d) => d.keys ?? []));
  let rcuChannels = $derived(
    (rcuChannelsQ.data ?? []).map((c) => ({ load: c.load, object_id: c.rcu }))
  );

  let workflow = $derived(
    computeUnitWorkflow({
      unit: unit,
      zones: unitZones,
      rooms: unitRooms,
      loads: unitLoads,
      inputDevices: unitDevices,
      inputKeys: unitInputKeys,
      rcus: rcusQ.data ?? [],
      dinModules: dinModulesQ.data ?? [],
      channels: rcuChannels,
      scenes: scenesQ.data ?? [],
      sceneLoads: sceneLoadsQ.data ?? [],
      products: productsQ.data ?? []
    })
  );

  let unitRcus = $derived(unit ? (rcusQ.data ?? []).filter((rcu) => rcu.unit === unit.id) : []);
  let unitDinModules = $derived(dinModulesQ.data ?? []);
  let unitScenes = $derived(
    unit ? (scenesQ.data ?? []).filter((scene) => scene.unit === unit.id) : []
  );

  let roomsByZone = $derived(groupBy(unitRooms, (room: Record<string, unknown>) => room.zone));
  let loadsByRoom = $derived(groupBy(unitLoads, (load: Record<string, unknown>) => load.room));
  let dinsByRcu = $derived(groupBy(unitDinModules, (din: Record<string, unknown>) => din.rcu));
  let keysByKeypad = $derived(groupBy(unitInputKeys, (key: Record<string, unknown>) => key.device));
  let roomById = $derived(new Map(unitRooms.map((room) => [room.id, room])));

  let visibleRows = $derived(
    buildVisibleRows({
      unit: unit,
      zones: unitZones,
      roomsByZone,
      loadsByRoom,
      unitRcus,
      dinsByRcu,
      keypads: unitDevices,
      keysByKeypad,
      unitScenes,
      issues: workflow.issues,
      issueCountBySection: workflow.issueCountBySection,
      expanded,
      roomById
    })
  );

  let selectedRowKey = $derived(resolveSelectedRowKey(entity, visibleRows));

  // The tree starts collapsed; when an entity gets selected (deep link,
  // command palette, create flow) expand the folders along the path to it so
  // the highlighted row is actually visible.
  const GROUP_FOR_ENTITY_TYPE: Partial<Record<NonNullable<EntityType>, string>> = {
    zone: 'group:spaces',
    room: 'group:spaces',
    load: 'group:spaces',
    rcu: 'group:hardware',
    din: 'group:hardware',
    keypad: 'group:keypads',
    scene: 'group:scenes',
    'led-logic': 'group:led-logic'
  };

  function ancestorKeysFor(entity: RouteEntity): string[] | null {
    const group = entity.type ? GROUP_FOR_ENTITY_TYPE[entity.type] : undefined;
    if (!group) return ['unit'];
    const id = entity.id;
    if (id == null) return ['unit', group];
    switch (entity.type) {
      case 'room': {
        const room = roomById.get(id);
        return room ? ['unit', group, `zone:${room.zone}`] : null;
      }
      case 'load': {
        const load = unitLoads.find((item) => item.id === id);
        const room = load ? roomById.get(load.room) : null;
        return room ? ['unit', group, `zone:${room.zone}`, `room:${room.id}`] : null;
      }
      case 'din': {
        const din = unitDinModules.find((item) => item.id === id);
        return din ? ['unit', group, `rcu:${din.rcu}`] : null;
      }
      default:
        return ['unit', group];
    }
  }

  let revealedTarget = '';
  $effect(() => {
    const target = `${unitId}:${entity.type ?? ''}:${entity.id ?? ''}`;
    if (target === revealedTarget) return;
    const keys = ancestorKeysFor(entity);
    if (keys === null) return; // entity data still loading — retry when it arrives
    revealedTarget = target;
    if (keys.every((key) => expanded[key] === true)) return;
    expanded = { ...expanded, ...Object.fromEntries(keys.map((key) => [key, true])) };
  });

  $effect(() => {
    if (selectedRowKey && rowEls[selectedRowKey]) {
      rowEls[selectedRowKey].scrollIntoView({ block: 'nearest' });
    }
  });

  onMount(() => {
    function onDocClick(e) {
      if (openMenuKey && !e.target.closest('[data-tree-menu]')) {
        openMenuKey = null;
      }
    }

    function onExpandIssues() {
      expanded = { ...expanded, 'group:issues': true };
      const first = workflow.issues[0];
      if (first?.entity_id != null) {
        selectEntity(first.entity_type, first.entity_id);
      }
    }

    function onFocusSection(e) {
      const group = e.detail?.group;
      if (!group) return;
      const key = `group:${group}`;
      expanded = { ...expanded, unit: true, [key]: true };
      requestAnimationFrame(() => {
        rowEls[key]?.scrollIntoView({ block: 'nearest' });
      });
    }

    document.addEventListener('click', onDocClick);
    document.addEventListener('expand-rail-issues', onExpandIssues);
    document.addEventListener('focus-rail-section', onFocusSection);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('expand-rail-issues', onExpandIssues);
      document.removeEventListener('focus-rail-section', onFocusSection);
    };
  });

  function groupBy(items, keyFn) {
    /** @type {Record<string, typeof items>} */
    const map = {};
    for (const item of items) {
      const key = String(keyFn(item));
      if (!map[key]) map[key] = [];
      map[key].push(item);
    }
    return map;
  }

  function sectionBadgeCount(sectionKeys, issueCountBySection) {
    return sectionKeys.reduce((sum, key) => sum + (issueCountBySection[key] || 0), 0);
  }

  function scenesBadgeCount(issues) {
    return issues
      .filter((issue) => issue.entity_type === 'scene')
      .reduce((sum, issue) => sum + (issue.count || 1), 0);
  }

  function deviceLabel(dev) {
    const typeLabel = DEVICE_TYPE_LABELS[dev.type] ?? 'Device';
    const room = roomById.get(dev.location_room);
    return room ? `${typeLabel} in ${room.name}` : `${typeLabel} ${dev.keys[0]?.address ?? dev.id}`;
  }

  function nextDeviceAddress() {
    const used = new Set(
      unitDevices.map((d) => d.keys[0]?.address).filter((a): a is number => a != null)
    );
    for (let i = 1; i <= 64; i++) {
      if (!used.has(i)) return i;
    }
    return 1;
  }

  function isExpanded(key) {
    return expanded[key] === true;
  }

  function toggleExpanded(key) {
    expanded = { ...expanded, [key]: !isExpanded(key) };
  }

  function rowKey(row) {
    return row.key;
  }

  function isRowSelected(row) {
    if (row.kind === 'unit') {
      return !entity.type;
    }
    if (row.kind === 'key') {
      return entity.type === 'keypad' && entity.id === row.selectId;
    }
    if (row.kind === 'led-key') {
      return entity.type === 'led-logic' && entity.id === row.selectId;
    }
    return entity.type === row.selectType && entity.id === row.selectId;
  }

  function resolveSelectedRowKey(entity, rows) {
    if (!entity.type) {
      return rows.find((row) => row.kind === 'unit')?.key ?? null;
    }
    if (entity.type === 'keypad') {
      return rows.find((row) => row.kind === 'keypad' && row.selectId === entity.id)?.key ?? null;
    }
    if (entity.type === 'led-logic' && entity.id != null) {
      return (
        rows.find((row) => row.kind === 'led-logic-device' && row.selectId === entity.id)?.key ??
        null
      );
    }
    if (entity.id == null) {
      return rows.find((row) => row.selectType === entity.type)?.key ?? null;
    }
    return (
      rows.find((row) => row.selectType === entity.type && row.selectId === entity.id)?.key ?? null
    );
  }

  function handleRowSelect(row) {
    openMenuKey = null;
    if (row.kind === 'unit') {
      clearEntity();
      return;
    }
    if (row.kind === 'issue') {
      if (row.selectType && row.selectId != null) {
        selectEntity(row.selectType, row.selectId);
      }
      return;
    }
    if (row.selectType) {
      selectEntity(row.selectType, row.selectId ?? null);
    }
  }

  function setRowEl(key, el) {
    if (el) rowEls[key] = el;
    else delete rowEls[key];
  }

  /** @param {HTMLElement} node @param {string} key */
  function registerRow(node, key) {
    setRowEl(key, node);
    return {
      update(nextKey) {
        if (nextKey !== key) {
          setRowEl(key, null);
          key = nextKey;
          setRowEl(key, node);
        }
      },
      destroy() {
        setRowEl(key, null);
      }
    };
  }

  function toggleMenu(key, event) {
    event.stopPropagation();
    openMenuKey = openMenuKey === key ? null : key;
  }

  async function promptName(label, defaultValue = '') {
    const value = window.prompt(label, defaultValue);
    if (value == null) return null;
    const trimmed = value.trim();
    return trimmed || null;
  }

  async function createZone() {
    const name = await promptName('Zone name');
    if (!name || !unit) return;
    createZoneMut.mutate(
      { unit: unit.id, name, description: '' },
      {
        onSuccess: (created) => {
          selectEntity('zone', created.id);
          showSuccess(`Zone "${created.name}" created`);
        },
        onError: (error) => showError(getErrorMessage(error))
      }
    );
  }

  async function createRoom(zoneId) {
    const name = await promptName('Room name');
    if (!name) return;
    createRoomMut.mutate(
      { zone: zoneId, name, description: '' },
      {
        onSuccess: (created) => {
          selectEntity('room', created.id);
          showSuccess(`Room "${created.name}" created`);
        },
        onError: (error) => showError(getErrorMessage(error))
      }
    );
  }

  function createKeypad(roomId) {
    createDevice(roomId);
  }

  async function createScene() {
    const name = await promptName('Scene name');
    if (!name || !unit) return;
    createSceneMut.mutate(
      {
        unit: unit.id,
        name,
        scope: 'unit',
        scope_id: unit.id,
        priority: 1
      },
      {
        onSuccess: (created) => {
          selectEntity('scene', created.id);
          showSuccess(`Scene "${created.name}" created`);
        },
        onError: (error) => showError(getErrorMessage(error))
      }
    );
  }

  async function renameEntity(type, id, currentName) {
    const name = await promptName('New name', currentName);
    if (!name) return;
    const onSuccess = () => showSuccess('Renamed');
    const onError = (error) => showError(getErrorMessage(error));

    switch (type) {
      case 'zone':
        updateZoneMut.mutate({ id, request: { name } }, { onSuccess, onError });
        break;
      case 'room':
        updateRoomMut.mutate({ id, request: { name } }, { onSuccess, onError });
        break;
      case 'load':
        updateLoadMut.mutate({ id, request: { name } }, { onSuccess, onError });
        break;
      case 'rcu':
        updateRcuMut.mutate({ id, request: { name } }, { onSuccess, onError });
        break;
      case 'keypad':
        showError('Rename keypads from the inspector.');
        return;
      case 'scene':
        updateSceneMut.mutate({ id, request: { name } }, { onSuccess, onError });
        break;
      default:
        break;
    }
  }

  async function changeDeviceAddress(dev) {
    const currentAddress = dev.keys[0]?.address;
    const addressStr = window.prompt('New address (1-255)', String(currentAddress ?? ''));
    if (!addressStr) return;
    const address = parseInt(addressStr, 10);
    if (isNaN(address) || address < 1 || address > 255) {
      showError('Address must be a number between 1 and 255');
      return;
    }
    if (address === currentAddress) return;
    try {
      await Promise.all(dev.keys.map((k) => updateInputKey(k.id, { address })));
      if (router.unitId) invalidateAfterInputDeviceUpdate(router.unitId);
      showSuccess(`Address changed to ${address}`);
    } catch (e) {
      showError(getErrorMessage(e));
    }
  }

  async function duplicateRoom(roomId) {
    try {
      const cloned = await cloneRoom(roomId);
      if (unitId) invalidateAfterRoomCreate(unitId);
      selectEntity('room', cloned.id);
      showSuccess(`Room duplicated as "${cloned.name}"`);
    } catch (error) {
      showError(getErrorMessage(error));
    }
  }

  async function duplicateScene(sceneId) {
    try {
      const cloned = await cloneScene(sceneId);
      if (unitId) invalidateAfterSceneCreate(unitId);
      selectEntity('scene', cloned.id);
      showSuccess(`Scene duplicated as "${cloned.name}"`);
    } catch (error) {
      showError(getErrorMessage(error));
    }
  }

  function requestDelete(type, id, label) {
    deleteTarget = { type, id, label };
    showDeleteConfirm = true;
    openMenuKey = null;
  }

  function handleDeleteSuccess(type, id) {
    if (entity.type === type && entity.id === id) {
      clearEntity();
    }
    showSuccess('Deleted');
  }

  async function handleDeleteConfirm() {
    showDeleteConfirm = false;
    if (!deleteTarget) return;
    const { type, id } = deleteTarget;
    deleteTarget = null;

    const onSuccess = () => handleDeleteSuccess(type, id);
    const onError = (error) => showError(getErrorMessage(error));

    switch (type) {
      case 'zone':
        deleteZoneMut.mutate(id, { onSuccess, onError });
        break;
      case 'room':
        deleteRoomMut.mutate(id, { onSuccess, onError });
        break;
      case 'load':
        deleteLoadMut.mutate(id, { onSuccess, onError });
        break;
      case 'rcu':
        deleteRcuMut.mutate(id, {
          onSuccess: () => {
            if (unitId) invalidateAfterChannelDelete(unitId);
            onSuccess();
          },
          onError
        });
        break;
      case 'din': {
        const din = unitDinModules.find((item) => item.id === id);
        try {
          await deleteDinModule(id);
          if (unitId && din) {
            invalidateAfterDinModuleCreate(unitId, din.rcu);
            invalidateAfterChannelDelete(unitId);
          }
          onSuccess();
        } catch (error) {
          onError(error);
        }
        break;
      }
      case 'keypad':
        deleteInputDeviceMut.mutate(id, { onSuccess, onError });
        break;
      case 'scene':
        deleteSceneMut.mutate(id, { onSuccess, onError });
        break;
      default:
        break;
    }
  }

  function runMenuAction(event, action) {
    event.stopPropagation();
    openMenuKey = null;
    action();
  }

  function buildVisibleRows(ctx) {
    /** @type {Array<any>} */
    const rows = [];

    function push(row) {
      rows.push(row);
    }

    function pushGroup(id, label, badgeCount, childBuilder) {
      const expandKey = `group:${id}`;
      push({
        key: expandKey,
        kind: 'group',
        label,
        depth: 1,
        expandable: true,
        expandKey,
        badgeCount,
        selectType: null,
        selectId: null,
        actions: groupActions(id)
      });
      if (isExpanded(expandKey)) childBuilder();
    }

    if (!ctx.unit) return rows;

    push({
      key: 'unit',
      kind: 'unit',
      label: ctx.unit.name,
      depth: 0,
      expandable: true,
      expandKey: 'unit',
      selectType: null,
      selectId: null,
      actions: [],
      isUnitName: true
    });

    if (!isExpanded('unit')) return rows;

    pushGroup(
      'spaces',
      'Spaces',
      sectionBadgeCount(GROUP_SECTION_BADGES.spaces, ctx.issueCountBySection),
      () => {
        for (const zone of ctx.zones) {
          const zoneKey = `zone:${zone.id}`;
          push({
            key: zoneKey,
            kind: 'zone',
            label: zone.name,
            depth: 2,
            expandable: true,
            expandKey: zoneKey,
            selectType: 'zone',
            selectId: zone.id,
            actions: zoneActions(zone)
          });

          if (!isExpanded(zoneKey)) continue;

          const zoneRooms = ctx.roomsByZone[String(zone.id)] || [];
          for (const room of zoneRooms) {
            const roomKey = `room:${room.id}`;
            push({
              key: roomKey,
              kind: 'room',
              label: room.name,
              depth: 3,
              expandable: true,
              expandKey: roomKey,
              selectType: 'room',
              selectId: room.id,
              actions: roomActions(room)
            });

            if (!isExpanded(roomKey)) continue;

            const roomLoads = ctx.loadsByRoom[String(room.id)] || [];
            for (const load of roomLoads) {
              push({
                key: `load:${load.id}`,
                kind: 'load',
                label: load.name,
                depth: 4,
                expandable: false,
                selectType: 'load',
                selectId: load.id,
                actions: loadActions(load)
              });
            }
          }
        }
      }
    );

    pushGroup(
      'hardware',
      'Hardware',
      sectionBadgeCount(GROUP_SECTION_BADGES.hardware, ctx.issueCountBySection),
      () => {
        for (const rcu of ctx.unitRcus) {
          const rcuKey = `rcu:${rcu.id}`;
          push({
            key: rcuKey,
            kind: 'rcu',
            label: rcu.name || `RCU ${rcu.id}`,
            depth: 2,
            expandable: true,
            expandKey: rcuKey,
            selectType: 'rcu',
            selectId: rcu.id,
            actions: rcuActions(rcu)
          });

          if (!isExpanded(rcuKey)) continue;

          const rcuDins = ctx.dinsByRcu[String(rcu.id)] || [];
          for (const din of rcuDins) {
            const typeLabel = dinModuleLabel(din.module_type);
            push({
              key: `din:${din.id}`,
              kind: 'din',
              label: `${typeLabel} DIN ${din.address ?? din.id}`,
              depth: 3,
              expandable: false,
              selectType: 'din',
              selectId: din.id,
              actions: dinActions(din),
              mono: true
            });
          }
        }

      }
    );

    pushGroup(
      'keypads',
      'Devices',
      sectionBadgeCount(GROUP_SECTION_BADGES.keypads, ctx.issueCountBySection),
      () => {
        for (const keypad of ctx.keypads) {
          const keypadKey = `keypad:${keypad.id}`;
          const room = ctx.roomById.get(keypad.location_room);
          push({
            key: keypadKey,
            kind: 'keypad',
            label: deviceLabel(keypad),
            depth: 2,
            expandable: true,
            expandKey: keypadKey,
            selectType: 'keypad',
            selectId: keypad.id,
            actions: keypadActions(keypad)
          });

          if (!isExpanded(keypadKey)) continue;

          const keys = ctx.keysByKeypad[String(keypad.id)] || [];
          for (const key of keys) {
            const index = (key.key_index ?? key.key_number ?? 0) + 1;
            push({
              key: `key:${key.id}`,
              kind: 'key',
              label: key.name?.trim() ? key.name : `Key ${index}`,
              depth: 3,
              expandable: false,
              selectType: 'keypad',
              selectId: keypad.id,
              actions: [],
              mono: true
            });
          }
        }
      }
    );

    pushGroup('scenes', 'Scenes', scenesBadgeCount(ctx.issues), () => {
      for (const scene of ctx.unitScenes) {
        push({
          key: `scene:${scene.id}`,
          kind: 'scene',
          label: scene.name,
          depth: 2,
          expandable: false,
          selectType: 'scene',
          selectId: scene.id,
          actions: sceneActions(scene)
        });
      }
    });

    pushGroup('led-logic', 'LED Logic', 0, () => {
      for (const device of ctx.keypads) {
        const deviceKey = `led-logic:${device.id}`;
        push({
          key: deviceKey,
          kind: 'led-logic-device',
          label: deviceLabel(device),
          depth: 2,
          expandable: true,
          expandKey: deviceKey,
          selectType: 'led-logic',
          selectId: device.id,
          actions: []
        });

        if (!isExpanded(deviceKey)) continue;

        const keys = ctx.keysByKeypad[String(device.id)] || [];
        for (const key of keys) {
          const index = (key.key_index ?? key.key_number ?? 0) + 1;
          push({
            key: `led-key:${key.id}`,
            kind: 'led-key',
            label: key.name?.trim() ? key.name : `Key ${index}`,
            depth: 3,
            expandable: false,
            selectType: 'led-logic',
            selectId: device.id,
            actions: [],
            mono: true
          });
        }
      }
    });

    rows.push({ key: '__issues_divider__', kind: 'divider' });

    push({
      key: 'group:issues',
      kind: 'group',
      label: `Issues (${ctx.issues.length})`,
      depth: 1,
      expandable: true,
      expandKey: 'group:issues',
      badgeCount: ctx.issues.length,
      selectType: null,
      selectId: null,
      actions: []
    });

    if (isExpanded('group:issues')) {
      for (const issue of ctx.issues) {
        push({
          key: `issue:${issue.id}`,
          kind: 'issue',
          label: issue.label,
          depth: 2,
          expandable: false,
          selectType: issue.entity_id != null ? issue.entity_type : null,
          selectId: issue.entity_id,
          actions: [],
          issueSeverity: issue.severity
        });
      }
    }

    return rows;
  }

  function groupActions(groupId) {
    if (groupId === 'spaces') {
      return [{ id: 'new-zone', label: 'New zone', run: () => createZone() }];
    }
    if (groupId === 'keypads' && unitRooms.length > 0) {
      return [
        {
          id: 'new-keypad',
          label: 'New device',
          run: () => createKeypad(unitRooms[0].id)
        }
      ];
    }
    if (groupId === 'scenes') {
      return [{ id: 'new-scene', label: 'New scene', run: () => createScene() }];
    }
    if (groupId === 'led-logic') {
      return [
        { id: 'led-generate', label: 'Generate scenes', run: () => handleLedGenerate() },
        { id: 'led-delete-all', label: 'Delete scenes', run: () => handleLedDeleteAll() }
      ];
    }
    return [];
  }

  function refreshLedScenes() {
    const unitId = router.unitId ?? 0;
    if (unitId > 0) {
      getQueryClient().invalidateQueries({ queryKey: ['led_scenes', { unitId }] });
    }
  }

  const manualLedSceneCount = $derived(
    (ledScenesQ.data ?? []).filter((s) => s.is_manually_edited).length
  );

  async function executeLedRegenerate(force: boolean) {
    const unitId = router.unitId ?? 0;
    if (unitId <= 0) return;

    regeneratingLed = true;
    try {
      const result = await regenerateLEDMappings(unitId, force);
      showSuccess(
        `LED scenes generated: ${result.created} loads created, ${result.skipped} skipped, ${result.deleted} removed`
      );
      refreshLedScenes();
    } catch (error) {
      if (error instanceof LEDMappingRegenerateConflict) {
        const ok = window.confirm(
          `${error.manual_count} LED scene(s) were manually edited. Overwrite them?`
        );
        if (ok) {
          await executeLedRegenerate(true);
        }
        return;
      }
      showError(getErrorMessage(error));
    } finally {
      regeneratingLed = false;
    }
  }

  async function handleLedGenerate() {
    if (manualLedSceneCount > 0) {
      const ok = window.confirm(
        `${manualLedSceneCount} LED scene(s) were manually edited. Overwrite them?`
      );
      if (!ok) return;
      await executeLedRegenerate(true);
      return;
    }
    await executeLedRegenerate(false);
  }

  async function handleLedDeleteAll() {
    const unitId = router.unitId ?? 0;
    if (unitId <= 0) return;
    const count = ledScenesQ.data?.length ?? 0;
    const ok = window.confirm(
      `Delete all ${count} LED scene(s) for this unit? This cannot be undone.`
    );
    if (!ok) return;
    deletingAllLed = true;
    try {
      await deleteAllLEDScenes(unitId);
      showSuccess(`Deleted ${count} LED scene(s)`);
      refreshLedScenes();
    } catch (error) {
      showError(getErrorMessage(error));
    } finally {
      deletingAllLed = false;
    }
  }

  function zoneActions(zone) {
    return [
      { id: 'new-room', label: 'New room', run: () => createRoom(zone.id) },
      {
        id: 'rename',
        label: 'Rename',
        run: () => renameEntity('zone', zone.id, zone.name)
      },
      {
        id: 'delete',
        label: 'Delete',
        run: () => requestDelete('zone', zone.id, zone.name)
      }
    ];
  }

  function roomActions(room) {
    return [
      { id: 'new-load', label: 'New load', run: () => createLoad(room.id) },
      {
        id: 'new-keypad',
        label: 'New device',
        run: () => createKeypad(room.id)
      },
      {
        id: 'duplicate',
        label: 'Duplicate',
        run: () => duplicateRoom(room.id)
      },
      {
        id: 'rename',
        label: 'Rename',
        run: () => renameEntity('room', room.id, room.name)
      },
      {
        id: 'delete',
        label: 'Delete',
        run: () => requestDelete('room', room.id, room.name)
      }
    ];
  }

  function loadActions(load) {
    return [
      {
        id: 'rename',
        label: 'Rename',
        run: () => renameEntity('load', load.id, load.name)
      },
      {
        id: 'delete',
        label: 'Delete',
        run: () => requestDelete('load', load.id, load.name)
      }
    ];
  }

  function rcuActions(rcu) {
    return [
      {
        id: 'rename',
        label: 'Rename',
        run: () => renameEntity('rcu', rcu.id, rcu.name || `RCU ${rcu.id}`)
      },
      {
        id: 'delete',
        label: 'Delete',
        run: () => requestDelete('rcu', rcu.id, rcu.name || `RCU ${rcu.id}`)
      }
    ];
  }

  function dinActions(din) {
    const label = `${dinModuleLabel(din.module_type)} DIN ${din.address ?? din.id}`;
    return [
      {
        id: 'delete',
        label: 'Delete',
        run: () => requestDelete('din', din.id, label)
      }
    ];
  }

  function keypadActions(dev) {
    const label = deviceLabel(dev);
    return [
      {
        id: 'change-address',
        label: 'Change address',
        run: () => changeDeviceAddress(dev)
      },
      {
        id: 'delete',
        label: 'Delete',
        run: () => requestDelete('keypad', dev.id, label)
      }
    ];
  }

  function sceneActions(scene) {
    return [
      {
        id: 'duplicate',
        label: 'Duplicate',
        run: () => duplicateScene(scene.id)
      },
      {
        id: 'rename',
        label: 'Rename',
        run: () => renameEntity('scene', scene.id, scene.name)
      },
      {
        id: 'delete',
        label: 'Delete',
        run: () => requestDelete('scene', scene.id, scene.name)
      }
    ];
  }

  function focusRow(index) {
    focusedIndex = index;
    const row = visibleRows[index];
    if (row?.key && rowEls[row.key]) {
      rowEls[row.key].focus({ preventScroll: true });
    }
  }

  async function handleKeydown(event) {
    if (!visibleRows.length) return;
    if (event.target.closest('[data-tree-menu]')) return;

    const selectableIndex =
      focusedIndex >= 0 ? focusedIndex : visibleRows.findIndex((row) => isRowSelected(row));
    let index = selectableIndex >= 0 ? selectableIndex : 0;

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        focusRow(Math.min(index + 1, visibleRows.length - 1));
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        focusRow(Math.max(index - 1, 0));
        break;
      }
      case 'ArrowRight': {
        event.preventDefault();
        const row = visibleRows[index];
        if (row?.expandable && !isExpanded(row.expandKey)) {
          toggleExpanded(row.expandKey);
        }
        break;
      }
      case 'ArrowLeft': {
        event.preventDefault();
        const row = visibleRows[index];
        if (row?.expandable && isExpanded(row.expandKey)) {
          toggleExpanded(row.expandKey);
        }
        break;
      }
      case 'Enter': {
        event.preventDefault();
        const row = visibleRows[index];
        if (row) handleRowSelect(row);
        break;
      }
      case 'Delete': {
        event.preventDefault();
        const row = visibleRows[index];
        if (!row?.selectType || row.selectId == null) return;
        if (['group', 'issue', 'key', 'unit'].includes(row.kind)) return;
        requestDelete(row.selectType, row.selectId, row.label);
        break;
      }
      default:
        break;
    }
  }

  $effect(() => {
    if (selectedRowKey) {
      const idx = visibleRows.findIndex((row) => row.key === selectedRowKey);
      if (idx >= 0) focusedIndex = idx;
    }
  });
</script>

<!-- svelte-ignore a11y_no_noninteractive_tabindex -->
<div
  bind:this={railEl}
  class="h-full outline-none"
  tabindex="0"
  role="tree"
  aria-label="Unit hierarchy"
  onkeydown={handleKeydown}
>
  {#if !unit}
    <div class="px-3 py-2 text-xs text-text-muted">No unit selected</div>
  {:else}
    <div class="py-1">
      {#each visibleRows as row (row.key)}
        {#if row.kind === 'divider'}
          <div class="mx-3 my-1 border-t border-border-default" aria-hidden="true"></div>
        {:else}
          {@const selected = isRowSelected(row)}
          {@const chevronOpen = row.expandable && isExpanded(row.expandKey)}
          <div
            use:registerRow={row.key}
            class="group relative flex h-[28px] items-center pr-1 cursor-pointer transition-colors duration-100 outline-none
            {selected ? 'bg-bg-selected' : 'hover:bg-bg-hover'}"
            style="padding-left: {12 + row.depth * 12}px"
            role="treeitem"
            aria-selected={selected}
            tabindex="-1"
            onclick={() => handleRowSelect(row)}
            ondblclick={(e) => {
              e.stopPropagation();
              if (row.expandable) toggleExpanded(row.expandKey);
            }}
          >
            <span
              class="absolute left-0 top-0 bottom-0 w-0.5 bg-accent-primary origin-center transition-transform duration-[var(--duration-quick)] ease-[var(--ease-out)] {selected
                ? 'scale-y-100'
                : 'scale-y-0'}"
              aria-hidden="true"
            ></span>
            {#if row.expandable}
              <button
                type="button"
                class="mr-1 flex h-4 w-4 shrink-0 items-center justify-center text-text-muted hover:text-text-primary"
                aria-label={chevronOpen ? 'Collapse' : 'Expand'}
                onclick={(e) => {
                  e.stopPropagation();
                  toggleExpanded(row.expandKey);
                }}
              >
                <ChevronRight
                  class="h-3 w-3 transition-transform duration-100 {chevronOpen ? 'rotate-90' : ''}"
                />
              </button>
            {:else}
              {#if row.key === 'group:led-logic'}
                <span class="mr-1 inline-flex h-4 w-4 shrink-0 items-center justify-center text-text-muted">
                  <Zap class="h-3 w-3" />
                </span>
              {:else}
                <span class="mr-1 inline-block w-4 shrink-0 text-center text-text-muted">·</span>
              {/if}
            {/if}

            <span
              class="min-w-0 flex-1 truncate {row.isUnitName
                ? 'text-sm font-medium text-text-primary'
                : 'text-xs text-text-secondary'} {row.mono
                ? 'font-mono'
                : ''} {row.issueSeverity === 'error'
                ? 'text-state-error'
                : ''} {row.issueSeverity === 'warning' ? 'text-state-warn' : ''}"
            >
              {row.label}
            </span>

            {#if row.badgeCount > 0 && row.kind === 'group' && row.key !== 'group:issues'}
              <span
                class="mr-1 shrink-0 rounded px-1.5 py-0.5 text-2xs font-medium
                {row.badgeCount > 0 ? 'bg-state-warn/15 text-state-warn' : ''}"
              >
                {row.badgeCount}
              </span>
            {/if}

            {#if row.actions?.length}
              <div class="relative shrink-0" data-tree-menu>
                <button
                  type="button"
                  class="flex h-5 w-5 items-center justify-center rounded text-text-muted opacity-0 transition-opacity duration-100 group-hover:opacity-100 hover:bg-bg-hover hover:text-text-primary"
                  aria-label="Row actions"
                  onclick={(e) => toggleMenu(row.key, e)}
                >
                  <MoreHorizontal class="h-3.5 w-3.5" />
                </button>

                {#if openMenuKey === row.key}
                  <div
                    class="absolute right-0 top-full z-20 mt-0.5 min-w-[140px] rounded border border-border-default bg-bg-base py-1 shadow-toyama-md"
                    data-tree-menu
                  >
                    {#each row.actions as action (action.id)}
                      <button
                        type="button"
                        class="block w-full px-3 py-1.5 text-left text-xs text-text-secondary hover:bg-bg-hover hover:text-text-primary"
                        onclick={(e) => runMenuAction(e, action.run)}
                      >
                        {action.label}
                      </button>
                    {/each}
                  </div>
                {/if}
              </div>
            {/if}
          </div>
        {/if}
      {/each}
    </div>
  {/if}
</div>

<ConfirmDialog
  bind:open={showDeleteConfirm}
  title="Delete {deleteTarget?.type ?? 'item'}"
  message="Are you sure you want to delete “{deleteTarget?.label}”? This cannot be undone."
  confirmText="Delete"
  cancelText="Cancel"
  variant="danger"
  onconfirm={handleDeleteConfirm}
  oncancel={() => {
    showDeleteConfirm = false;
    deleteTarget = null;
  }}
/>
